import React, { useState } from "react";

function formatNum(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString("en-US", { maximumFractionDigits: 4 });
}

// Three levels, because a single sell shouldn't look like an alarm:
//   net 0      -> neutral (no colour)
//   net 1      -> amber   "worth a look"
//   net 2+     -> green / red  "a real lean"
// Returns the class suffix shared by the icon and the count badge so the two
// can never disagree.
function signalTone(signal, strength) {
  if (signal !== "bullish" && signal !== "bearish") return null;
  if (strength >= 2) return signal; // "bullish" | "bearish"
  return "watch"; // amber
}

function signalTitle(signal, bullish, bearish, tone) {
  const lean =
    signal === "bullish"
      ? `${bullish} קניות/העלאות מול ${bearish} מכירות/הורדות`
      : `${bearish} מכירות/הורדות מול ${bullish} קניות/העלאות`;
  if (tone === "watch") return `סימן חלש — ${lean}`;
  return signal === "bullish" ? `סימן חיובי — ${lean}` : `סימן שלילי — ${lean}`;
}

function SignalIcon({ signal, tone, title, onClick }) {
  if (!tone) return null;
  const bullishSignal = signal === "bullish";

  return (
    <button
      type="button"
      className={`signal-icon ${tone}`}
      onClick={onClick}
      title={title}
      aria-label={title}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
        {bullishSignal ? (
          <>
            <polyline points="3 17 9 11 13 15 21 7" />
            <polyline points="15 7 21 7 21 13" />
          </>
        ) : (
          <>
            <polyline points="3 7 9 13 13 9 21 17" />
            <polyline points="15 17 21 17 21 11" />
          </>
        )}
      </svg>
    </button>
  );
}

// investing.com's own verdicts, mapped to the same three-tone language the
// news signal already speaks so the card reads as one thing.
const VERDICT_TONE = {
  strong_buy: "bullish",
  buy: "bullish",
  neutral: "watch",
  sell: "bearish",
  strong_sell: "bearish",
};

function formatTarget(n) {
  if (n === null || n === undefined) return null;
  return Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function ScorePanel({ score, symbol }) {
  if (!score) return null;
  const target = formatTarget(score.price_target);
  const upside = score.upside_pct;
  const hasUpside = upside !== null && upside !== undefined;
  const when = score.scraped_at
    ? new Date(score.scraped_at).toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" })
    : null;

  return (
    <div className="score-row" title={when ? `נמשך מ-investing.com ב-${when}` : undefined}>
      {score.technical_label && (
        <span className={`score-chip ${VERDICT_TONE[score.technical] || "watch"}`}>
          <span className="score-label">ניתוח טכני</span>
          <strong>{score.technical_label}</strong>
        </span>
      )}
      {score.analyst_sentiment_label && (
        <span className={`score-chip ${VERDICT_TONE[score.analyst_sentiment] || "watch"}`}>
          <span className="score-label">אנליסטים</span>
          <strong>{score.analyst_sentiment_label}</strong>
        </span>
      )}
      {target && (
        <span className={`score-chip ${hasUpside ? (upside >= 0 ? "bullish" : "bearish") : ""}`}>
          <span className="score-label">מחיר יעד</span>
          <strong dir="ltr">{target}</strong>
          {hasUpside && (
            <span className="score-move" dir="ltr">
              {upside >= 0 ? "+" : ""}
              {Number(upside).toFixed(2)}%
            </span>
          )}
        </span>
      )}
    </div>
  );
}

function StockRow({
  stock,
  onSave,
  onDelete,
  onMove,
  newsCount = 0,
  onOpenNews,
  signal = "neutral",
  strength = 0,
  bullish = 0,
  bearish = 0,
  score = null,
}) {
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState(stock.purchase_price ?? "");
  const [qty, setQty] = useState(stock.quantity ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const tone = signalTone(signal, strength);
  const signalHint = tone ? signalTitle(signal, bullish, bearish, tone) : null;

  const startEdit = () => {
    setPrice(stock.purchase_price ?? "");
    setQty(stock.quantity ?? "");
    setError(null);
    setEditing(true);
  };

  // Explicit save rather than PATCH-on-blur: one request per edit, and one
  // clear place to surface a failure.
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave(stock.id, {
        purchase_price: price === "" ? null : price,
        quantity: qty === "" ? null : qty,
      });
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const considering = stock.watch_status === "considering_sell";

  const move = async () => {
    setBusy(true);
    setError(null);
    try {
      await onMove(stock.id, considering ? "holding" : "considering_sell");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`למחוק את ${stock.symbol || stock.name}?`)) return;
    setBusy(true);
    try {
      await onDelete(stock.id);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const total =
    stock.purchase_price !== null && stock.quantity !== null
      ? stock.purchase_price * stock.quantity
      : null;

  return (
    <article className={`stock-row${considering ? " considering" : ""}`}>
      <div className="stock-main">
        <div className="stock-titles">
          <span className="stock-symbol-row">
            <SignalIcon
              signal={signal}
              tone={tone}
              title={signalHint}
              onClick={() => onOpenNews && onOpenNews(stock.symbol)}
            />
            <span className="stock-symbol" dir="ltr">
              {stock.symbol || "—"}
            </span>
            {newsCount > 0 && (
              <button
                type="button"
                // The count badge carries the same tone as the icon, so the
                // colour reads at a glance without hunting for the arrow.
                className={`news-badge${tone ? ` ${tone}` : ""}`}
                onClick={() => onOpenNews && onOpenNews(stock.symbol)}
                title={signalHint || `${newsCount} אירועים`}
              >
                {newsCount}
              </button>
            )}
          </span>
          {/* dir="auto" so "Apple Inc." keeps its period on the right while a
              Hebrew name still renders RTL */}
          {stock.name && (
            <span className="stock-name" dir="auto">
              {stock.name}
            </span>
          )}
          <ScorePanel score={score} symbol={stock.symbol} />
        </div>

        {editing ? (
          <div className="stock-fields">
            <label className="stock-field">
              <span>מחיר רכישה</span>
              <input
                type="number"
                step="any"
                min="0"
                className="field-input"
                dir="ltr"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="—"
              />
            </label>
            <label className="stock-field">
              <span>כמות</span>
              <input
                type="number"
                step="any"
                min="0"
                className="field-input"
                dir="ltr"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                placeholder="—"
              />
            </label>
          </div>
        ) : (
          <div className="stock-fields">
            <div className="stock-field">
              <span>מחיר רכישה</span>
              <strong dir="ltr">{formatNum(stock.purchase_price)}</strong>
            </div>
            <div className="stock-field">
              <span>כמות</span>
              <strong dir="ltr">{formatNum(stock.quantity)}</strong>
            </div>
            <div className="stock-field">
              <span>עלות כוללת</span>
              <strong dir="ltr">{formatNum(total)}</strong>
            </div>
          </div>
        )}
      </div>

      <div className="stock-actions">
        {editing ? (
          <>
            <button className="icon-btn" onClick={save} disabled={busy} title="שמור">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </button>
            <button
              className="icon-btn"
              onClick={() => setEditing(false)}
              disabled={busy}
              title="ביטול"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </>
        ) : (
          <>
            <button
              className={`icon-btn${considering ? " active" : ""}`}
              onClick={move}
              disabled={busy}
              title={considering ? "החזר למניות שלי" : "העבר ל״שוקל למכור״"}
            >
              {considering ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 14l-4-4 4-4" />
                  <path d="M5 10h11a4 4 0 0 1 0 8h-1" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              )}
            </button>
            <button className="icon-btn" onClick={startEdit} disabled={busy} title="ערוך">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
            <button
              className="icon-btn danger"
              onClick={remove}
              disabled={busy}
              title="מחק"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </>
        )}
      </div>

      {error && <div className="stock-error">{error}</div>}
    </article>
  );
}

export default StockRow;
